import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SearchPinIcon from '../../../../../public/static/images/new_ad/icons/icon-select-location.svg';
import MetroIcon from '../../../../../public/static/images/new_ad/icons/icon-metro.svg';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import Icon from '../../../../Icon';
import { useLandmarks } from '../../../../../hooks/useLandmarks';
import {
  chipsStyles,
  LandmarksContainer,
  MapContainer,
  MapOverlay,
  SelectMapButton,
  SelectMapButtonText
} from './styled';
import { FormErrorField } from '../FormErrorField';
import { Chips } from '../../../../ui/Chips';
import { ChipsTheme } from '../../../../ui/Chips/types';
import { Map } from '../../../../ui/MapModal/Map';
import { useGoogleMaps } from '../../../../../providers/GoogleMapsProvider';
import { Coordinates } from '../../../../../providers/GoogleMapsProvider/types';

interface SelectLocationMapProps {
  initialLocation: Coordinates;
  shouldRenderMap: boolean;
  setShouldRenderMap: React.Dispatch<React.SetStateAction<boolean>>;
}

const DEFAULT_INITIAL_LOCATION: Coordinates = {
  lat: 40.409264,
  lng: 49.867092
};

export const SelectLocationMap: React.FC<SelectLocationMapProps> = ({
  initialLocation = DEFAULT_INITIAL_LOCATION,
  shouldRenderMap,
  setShouldRenderMap
}) => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { values } = useFormState();
  const {
    setIsInitializedMap,
    setMapData,
    isOpenModal,
    toggleModal,
    setMapModalData,
    setMapRef,
    mapRef
  } = useGoogleMaps();

  const [pinSelected, setPinSelected] = useState(
    !!values[FORM_VALUES.coordinates]
  );

  const savedCoordinates: Coordinates =
    values[FORM_VALUES.coordinates] || initialLocation;

  const { handleLocationDelete, getLandmarks, locations } = useLandmarks(
    savedCoordinates,
    initialLocation
  );

  useEffect(() => {
    change(
      FORM_VALUES.locationIds,
      locations?.map(location => location.id)
    );
  }, [locations, change]);

  const onToggleModal = useCallback(
    (newState?: boolean) => {
      setIsInitializedMap(true);
      toggleModal(newState);
    },
    [setIsInitializedMap, toggleModal]
  );

  const saveCoordinates = useCallback(() => {
    const newCoordinates = mapRef?.current;

    if (newCoordinates) {
      change(FORM_VALUES.coordinates, newCoordinates);

      setShouldRenderMap(true);
      setPinSelected(true);

      getLandmarks({
        variables: {
          cityId: values[FORM_VALUES.cityId],
          latitude: newCoordinates.lat,
          longitude: newCoordinates.lng
        }
      });
    }
    onToggleModal();
  }, [mapRef, change, setShouldRenderMap, getLandmarks, values]);

  const handleBack = useCallback(() => {
    setMapRef(savedCoordinates);
  }, [savedCoordinates, setMapRef]);

  useEffect(() => {
    setMapModalData({
      onClose: handleBack,
      saveCoordinates,
      modalId: 'item-form-map-modal'
    });
  }, [handleBack, saveCoordinates, setMapModalData]);

  useEffect(() => {
    setMapData({
      initialLocation,
      pinSelected,
      isMarkerDraggable: true
    });
  }, [isOpenModal, initialLocation, pinSelected, setMapData]);

  const handleContainerClick =
    isOpenModal || !pinSelected ? null : onToggleModal;

  return (
    <div>
      <MapContainer
        tabIndex={0}
        role="button"
        onClick={handleContainerClick}
        onKeyPress={handleContainerClick}
        data-cy="item-form-map"
      >
        {shouldRenderMap && (
          <>
            <MapOverlay $isPinSelected={pinSelected} />
            <Map />
          </>
        )}
        {!shouldRenderMap && !isOpenModal && (
          <MapOverlay>
            <SelectMapButton
              type="button"
              onClick={() => onToggleModal(true)}
              data-cy="new-ad-select-on-map"
            >
              <Icon
                width={20}
                height={20}
                IconComponent={SearchPinIcon}
                indent={false}
              />
              <SelectMapButtonText tag="body_1">
                {t('new_ad.select_on_map')}
              </SelectMapButtonText>
            </SelectMapButton>
          </MapOverlay>
        )}
      </MapContainer>

      <FormErrorField
        field={FORM_VALUES.coordinates}
        cypress="coordinates-field-error"
      />

      {!!locations?.length && (
        <LandmarksContainer data-cy="landmarks-container">
          {locations?.map(location => (
            <Chips
              key={location.id}
              text={location.name || ''}
              styles={chipsStyles}
              chipsTheme={ChipsTheme.White}
              icon={location.locationGroupId === '1' ? MetroIcon : null}
              resetHandler={() => handleLocationDelete(location.id)}
              cypress={`landmarks-${location.id}`}
              isTextPadding={false}
              name={`landmarks-${location.id}`}
              dirty
            />
          ))}
        </LandmarksContainer>
      )}
    </div>
  );
};
